import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Badge, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'

const SWAP_REASON_OPTIONS = [
  { value: 'direct_sell', label: 'Direct Sell' },
  { value: 'trade', label: 'Trade' },
  { value: 'buy_stock', label: 'Buy Stock' }
]

const propTypes = {
  isOpen             : PropTypes.bool.isRequired,
  toggle             : PropTypes.func.isRequired,
  sku                : PropTypes.string,
  handleConfirmation : PropTypes.func.isRequired
}

const defaultProps = {
  sku: 'This product'
}

const SwapBrvToBkvConfirmationModal = ({
  isOpen,
  toggle,
  sku,
  handleConfirmation
}) => {
  const [ isSubmitting, setIsSubmitting ] = useState(false)
  const [ swapReason, setSwapReason ] = useState('')
  const selectedReasonLabel = useMemo(
    () => SWAP_REASON_OPTIONS.find((option) => option.value === swapReason)?.label,
    [ swapReason ]
  )

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false)
      setSwapReason('')
    }
  }, [ isOpen ])

  const handleConfirm = async () => {
    if (!swapReason) return

    setIsSubmitting(true)

    try {
      await handleConfirmation(swapReason)
    } catch (error) {
      setIsSubmitting(false)
      return error
    }
  }

  return (
    <Modal isOpen={ isOpen } toggle={ toggle } centered size="lg">
      <ModalHeader toggle={ isSubmitting ? undefined : toggle }>
        Confirm Swap BRV to BKV
      </ModalHeader>

      <ModalBody>
        <div className="mb-4">
          <div className="d-flex align-items-center mb-3">
            <Badge color="warning" pill className="px-3 py-2 mr-2">BRV</Badge>
            <i className="fas fa-arrow-right text-muted mr-2" />
            <Badge color="success" pill className="px-3 py-2 mr-3">BKV</Badge>
            <span className="font-weight-bold text-dark">{ sku || 'This product' }</span>
          </div>

          <p className="mb-0 text-muted">
            Sistem akan membuat SKU BKV baru dari product BRV ini. Atribut QC, foto, dan posisi stock tetap dibawa, tetapi appraisal dan pricing akan dimulai ulang.
          </p>
        </div>

        <div className="form-group mb-3">
          <Label for="swap-brv-to-bkv-reason" className="font-weight-bold">
            Delete Reason
          </Label>
          <Input
            id="swap-brv-to-bkv-reason"
            type="select"
            value={ swapReason }
            disabled={ isSubmitting }
            onChange={ (event) => setSwapReason(event.target.value) }
          >
            <option value="">Select Delete Reason</option>
            {
              SWAP_REASON_OPTIONS.map((option) => (
                <option key={ option.value } value={ option.value }>
                  { option.label }
                </option>
              ))
            }
          </Input>
          <small className="form-text text-muted">
            Reason ini akan disimpan pada old BRV dan menentukan Inventory Type untuk new BKV.
          </small>
        </div>

        <div className="border rounded p-3 mb-3 bg-light">
          <div className="font-weight-bold mb-2">Yang akan dilakukan sistem</div>
          <ul className="mb-0 pl-3 text-muted">
            <li>
              Membuat product BKV baru dengan IQ dan SQ mengikuti kondisi stock BRV lama.
            </li>
            <li>
              Copy atribut BRV ke BKV, termasuk specs, kelengkapan,
              product size, relasi, dan foto. Buying Price/HM, Default Price, dan
              Selling Price/HJ dikosongkan; appraisal dimulai dari Appraisal Needed.
            </li>
            <li>
              Deal aktif dan Consignor Return aktif akan dipindahkan ke BKV,
              termasuk reference inventory movement terkait agar flow cancel / shipment tetap aman.
            </li>
            <li>
              Old BRV tetap <strong>Not Available</strong>, lalu set IQ 0 / SQ 0.
            </li>
            <li>
              Old BRV tetap tersimpan di database untuk audit/history,
              tapi di-hide dari inventory list operasional.
            </li>
            <li>
              Status Note pada inventory baru akan menampilkan source SKU dan reason
              { selectedReasonLabel ? ` (${selectedReasonLabel})` : '' }.
            </li>
          </ul>
        </div>

        <div className="alert alert-warning mb-0">
          Setelah confirm, proses ini tidak sekadar update tampilan.
          Sistem akan membuat SKU BKV baru dan menonaktifkan SKU BRV lama.
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          color="secondary"
          disabled={ isSubmitting }
          onClick={ toggle }
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={ isSubmitting || !swapReason }
          onClick={ handleConfirm }
        >
          {
            isSubmitting
              ? <><i className="ni ni-settings-gear-65 spin mr-2" />Swapping...</>
              : 'Confirm Swap'
          }
        </Button>
      </ModalFooter>
    </Modal>
  )
}

SwapBrvToBkvConfirmationModal.propTypes = propTypes
SwapBrvToBkvConfirmationModal.defaultProps = defaultProps

export default SwapBrvToBkvConfirmationModal
